
import React from "react";
import headingStyles from "./ModalHeadingStyles";

const NextGenerationPreschoolModalContent = () => (
  <div style={{ textAlign: 'left', fontFamily: 'Raleway, sans-serif' }}>
    <h3 style={headingStyles.h3}>Next Generation Preschool Science (NGPS)</h3>
    <hr />
    <div style={{ fontWeight: 'bold', color: '#000' }}>UX Research</div>
    <div>Spring 2015</div>
    <p><em>Formative evaluation of an emerging digital science curriculum for preschoolers</em></p>
    <hr />
    <div><strong style={{ fontWeight: 800 }}>Project Role:</strong> UX Researcher (Master's Candidate)</div>
    <div><strong style={{ fontWeight: 800 }}>Platforms:</strong> iOS Tablet (iPad)</div>
    <div><strong style={{ fontWeight: 800 }}>Deliverables:</strong> Data analysis, custom evaluation instruments, formative research report</div>
    <div><strong style={{ fontWeight: 800 }}>Timeline:</strong> 8 weeks</div>
    <hr />
    <h4 style={headingStyles.h4}>Project Overview</h4>
    <p>
      In partnership with public media producer WGBH, Education Development Center, and SRI International, this project evaluated a tablet-based curriculum designed to promote early science learning for preschoolers in underserved communities. The curriculum—part of a National Science Foundation-funded initiative called Next Generation Preschool Science—aimed to foster core science practices such as observing, predicting, experimenting, and analyzing data through a blended experience of digital tools and hands-on activities.
    </p>
    <p>
      Our goal was to understand how children and teachers interacted with the NGPS tools in real classroom settings, and to identify both opportunities and obstacles in seamlessly integrating iPad activities with physical science investigations focused on topics like ramps, plants, and shadows.
    </p>

    <h4 style={headingStyles.h4}>Problem Statement</h4>
    <p>
      Could a blended curriculum combining iPad-based documentation tools and real-world experiments support preschoolers in learning science concepts—and help teachers create a unified, holistic learning experience across media?
    </p>

    <h4 style={headingStyles.h4}>The Solution</h4>
    <p>
      We conducted a multi-method qualitative evaluation across two Boston-area preschool sites. In collaboration with three master's candidate students, I developed several of the custom evaluation instruments and conducted observational coding to assess children’s behavior, engagement, and attention shifting between tablet-based and hands-on activities. This included real-time behavioral tracking, teacher and child interviews, and documentation of how the tools supported (or detracted from) scientific inquiry in the classroom.
    </p>

    <h4 style={headingStyles.h4}>Aligning Goals</h4>
    <p>
      As a researcher embedded within an interdisciplinary team, I focused on surfacing actionable feedback for designers and curriculum developers. The team was seeking early, formative insights to refine the digital toolkit and ensure it could integrate developmentally appropriate technology use with rich, inquiry-based learning.
    </p>
    <p>
      The ultimate goal was to ensure that children were not simply toggling between disconnected digital and physical experiences, but engaging in meaningful science exploration across both contexts.
    </p>

    <h4 style={headingStyles.h4}>Discover</h4>
    <ul>
      <li>Conducted classroom observations, coding 60 behavior intervals per child to assess on-task tablet use versus off-task behavior</li>
      <li>Interviewed teachers about their perceptions of student behavior, learning outcomes, and classroom dynamics while using tablets</li>
      <li>Interviewed children to gather data on their enjoyment, understanding, and preferences in using the tablet modules</li>
      <li>Observed how often and how easily students and teachers shifted attention between hands-on and digital experiences</li>
    </ul>

    <h4 style={headingStyles.h4}>Define</h4>
    <ul>
      <li>Preschoolers were generally able to stay on task when using tablets for scientific observation and documentation, particularly when activities were well-scaffolded by the teacher</li>
      <li>Some behavioral challenges emerged (e.g., difficulty sharing, off-task tapping), highlighting the need for clearer user flows and role-sharing prompts</li>
      <li>Teachers reported that the digital tools added unique learning opportunities but required more training and support for full integration</li>
    </ul>

    <h4 style={headingStyles.h4}>Develop</h4>
    <ul>
      <li>Created tailored observational and interview instruments for both child and educator participants</li>
      <li>Triangulated findings across multiple methods: partial-interval coding, reactive observation, and semi-structured interviews</li>
      <li>Generated a rich qualitative dataset used by curriculum developers to refine both digital design and teacher guidance materials</li>
    </ul>

    <h4 style={headingStyles.h4}>Deliver</h4>
    <ul>
      <li>Findings informed changes to the NGPS iPad toolkit, including improvements to user interface flow and teacher-facing instructional materials</li>
      <li>Results supported a shift toward professional development resources that better helped teachers blend digital and hands-on elements</li>
      <li>Contributed to subsequent grant efforts, including the development of a dual-language learner extension of the program</li>
      <li>Research confirmed significantly greater science learning gains among students in NGPS classrooms vs. control classrooms</li>
    </ul>
  </div>
);

export default NextGenerationPreschoolModalContent;
